import * as React from 'react';
import {useSelector, useDispatch} from 'react-redux';

import {useQuery} from '@apollo/react-hooks';
import {destroy} from 'redux-form';

import type {EmailAddressee} from 'nutshell-core/types';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {LoadingPage} from 'shells/loading-page/loading-page';
import {Modal} from 'shells/modal/modal';

import {AiFeatureType} from 'nutshell-graphql-types';
import type {
    GetReplyableEmail as GetReplyableEmailQuery,
    GetReplyableEmailVariables as GetReplyableEmailQueryVariables,
    NutEmailAddress,
    NutEmailAddress_avatarEntity as NutEmailAddressAvatarEntity,
    RelatedEmailAddresses_all as RelatedEmailAddresses,
    RelatedEmailAddresses_all_entity as RelatedEmailAddressesEntity,
} from 'nutshell-graphql-types';

import type {NutshellState} from '../../../store';

import {GetSessionUser} from '../../core/get-session-user';
import {useIsAiFeatureEnabled} from '../../setup/ai-features/graphql/hooks/use-is-ai-feature-enabled';

import {closeReplyModal, minimizeReplyModal} from '../email-reply-actions';
import {
    getIsOpen,
    getReplyToEmailId,
    getIsReplyingAll,
    getOnReplySuccess,
    getPendingRestoreReplyFromMinimize,
} from '../email-reply-selectors';
import {useGetSuggestedReplies} from '../graphql/hooks';

import {createFromScratchTemplate} from './create-from-scratch-template';
import {EmailReplyModalErrorState} from './email-reply-modal-error-state';
import GET_REPLYABLE_EMAIL from './graphql/queries/get-replyable-email.graphql';
import {EmailSenderGraphqlNew} from './sender-with-groups/email-sender-graphql-new';
import {FORM_NAME, CREATE_FROM_SCRATCH_ID} from './types';

type Props = {
    isOpen: boolean;
    replyToEmailId: string;
    isReplyingAll: boolean;
    shouldRestoreFromStorage: boolean;
    handleClose: () => void;
    handleMinimize: () => void;
    handleSubmitSuccess: () => void;
};

function EmailReplyModalComponent(props: Props) {
    const {data, error, loading} = useQuery<
        GetReplyableEmailQuery,
        GetReplyableEmailQueryVariables
    >(GET_REPLYABLE_EMAIL, {
        variables: {emailId: props.replyToEmailId},
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only', // make sure we get the correct related leads if mappings changed
    });

    const {isEnabled: isEmailReplyStarterAssistEnabled} = useIsAiFeatureEnabled(
        AiFeatureType.EMAIL_REPLY_STARTER
    );
    const isEmailReplyStarterEnabled = NutClientConfig.hasAiOutcomesFF
        ? isEmailReplyStarterAssistEnabled
        : NutClientConfig.enableAiEmailReplies;

    const {suggestedEmailReplies, isLoading: isLoadingSuggestedReplies} = useGetSuggestedReplies(
        props.replyToEmailId,
        !isEmailReplyStarterEnabled || !NutClientConfig.hasAiEmailReplies
    );

    const replyEmail = data ? data.replyEmail : null;
    // @ts-expect-error
    const errorMessage = safelyGetGraphQLErrorMessage(error);

    const headerText = replyEmail ? `Reply to ${replyEmail.replyToAddress.display}` : 'Reply';

    return (
        <GetSessionUser>
            {/* @ts-expect-error */}
            {({user}) => {
                const createFromScratchForReply =
                    user && replyEmail && !loading
                        ? createFromScratchTemplate(user, {
                              subject: replyEmail.subject,
                              body: replyEmail.bodyHtml,
                          })
                        : undefined;

                const createFromScratchSequences =
                    createFromScratchForReply !== undefined
                        ? {[CREATE_FROM_SCRATCH_ID]: createFromScratchForReply}
                        : undefined;

                const replyQuotedSuffixHtml =
                    createFromScratchForReply !== undefined
                        ? createFromScratchForReply.emailTemplates[CREATE_FROM_SCRATCH_ID]?.body
                        : undefined;

                return user ? (
                    <Modal
                        headerText={headerText}
                        onMinimize={props.handleMinimize}
                        isWide={true}
                        isOpen={props.isOpen}
                        canClickOutsideToClose={false}
                        onClose={props.handleClose}
                        waitForMount={true}
                        // @ts-expect-error
                        maxHeight='90vh'
                    >
                        {loading ? <LoadingPage /> : undefined}
                        {errorMessage ? (
                            <EmailReplyModalErrorState errorMessage={errorMessage} />
                        ) : undefined}
                        {replyEmail && createFromScratchSequences ? (
                            <EmailSenderGraphqlNew
                                initialValues={{
                                    fromAddress: user.sendableEmailAddresses[0],
                                    toAddresses: [
                                        {
                                            value: nutEmailAddressToEmailSenderAddressType(
                                                replyEmail.replyToAddress
                                            ),
                                        },
                                    ],
                                    reply: {
                                        toAddresses: [
                                            {
                                                value: nutEmailAddressToEmailSenderAddressType(
                                                    replyEmail.replyToAddress
                                                ),
                                            },
                                        ],
                                        replyAllAddresses: replyEmail.replyAllAddresses.map(
                                            (address) => ({
                                                value: nutEmailAddressToEmailSenderAddressType(
                                                    address
                                                ),
                                            })
                                        ),
                                        isReplyAll: props.isReplyingAll,
                                    },
                                    ccAddresses: [],
                                    sequences: createFromScratchSequences,
                                    templateGroups: [],
                                    uncategorizedTemplates: [],
                                    recentlySentTemplates: [],
                                    relatedLeadIds: replyEmail.relatedLeads.edges
                                        .filter((edge) => edge.isMapped)
                                        .map((edge) => edge.node.id),
                                }}
                                createFromScratchTemplate={createFromScratchSequences}
                                onEmailEdited={() => {}}
                                isBodyFocused={true}
                                relatedEmailAddresses={{
                                    __typename: 'EmailAddressesEavConnection',
                                    all: replyEmail.replyAllAddresses.map((address) =>
                                        nutEmailAddressToRelatedEmailAddress(address)
                                    ),
                                    primary: null,
                                }}
                                relatedLeads={replyEmail.relatedLeads.edges.map(
                                    (edge) => edge.node
                                )}
                                user={user}
                                closeModal={props.handleSubmitSuccess}
                                shouldRestoreFromStorage={props.shouldRestoreFromStorage}
                                emailReplyId={props.replyToEmailId}
                                suggestedReplies={suggestedEmailReplies || undefined}
                                isLoadingSuggestedReplies={isLoadingSuggestedReplies}
                                replyQuotedSuffixHtml={replyQuotedSuffixHtml}
                            />
                        ) : undefined}
                    </Modal>
                ) : null;
            }}
        </GetSessionUser>
    );
}

export function EmailReplyModal() {
    const isOpen = useSelector((state: NutshellState) => getIsOpen(state));
    const replyToEmailId = useSelector((state: NutshellState) => getReplyToEmailId(state));
    const isReplyingAll = useSelector((state: NutshellState) => getIsReplyingAll(state));
    const onReplySuccess = useSelector((state: NutshellState) => getOnReplySuccess(state));
    const shouldRestoreFromStorage = useSelector((state: NutshellState) =>
        getPendingRestoreReplyFromMinimize(state)
    );

    const dispatch = useDispatch();

    const handleClose = () => {
        dispatch(closeReplyModal());
        dispatch(destroy(FORM_NAME));
    };

    const handleMinimize = () => {
        dispatch(minimizeReplyModal());
    };

    const handleSubmitSuccess = () => {
        if (onReplySuccess) {
            onReplySuccess();
        }

        handleClose();
    };

    if (!replyToEmailId) {
        return null;
    }

    return (
        <EmailReplyModalComponent
            isOpen={isOpen}
            replyToEmailId={replyToEmailId}
            isReplyingAll={isReplyingAll}
            shouldRestoreFromStorage={shouldRestoreFromStorage}
            handleClose={handleClose}
            handleMinimize={handleMinimize}
            handleSubmitSuccess={handleSubmitSuccess}
        />
    );
}

/**
 * This is a little confusing, and basically useless, but is needed or else flow
 * losses its mind. All this does is convert from a NutEmailAddress, which is
 * what we get from the replyable email query, to an EAV email address, which is
 * what our email sender form expects.
 *
 * @param {NutEmailAddress} emailAddress The NutEmailAddress object to convert
 * @returns Object The correctly formatted email address
 */
function nutEmailAddressToEmailSenderAddressType(emailAddress: NutEmailAddress): EmailAddressee {
    return {
        display: emailAddress.display,
        address: emailAddress.address,
        formatted: emailAddress.formatted,
        entity: emailAddress.avatarEntity
            ? {
                  id: emailAddress.avatarEntity.id,
                  type: emailAddress.avatarEntity.type,
                  name: emailAddress.avatarEntity.name,
                  initials: emailAddress.avatarEntity.initials,
                  avatarUrl: emailAddress.avatarEntity.avatarUrl,
                  htmlUrl: emailAddress.avatarEntity.htmlUrl,
              }
            : null,
    };
}

function nutEmailAddressToRelatedEmailAddress(
    emailAddress: NutEmailAddress
): RelatedEmailAddresses {
    return {
        __typename: 'EmailAddressEav',
        name: emailAddress.display,
        isPrimary: true,
        // @ts-expect-error
        value: {
            __typename: 'EmailAddressValue',
            ...nutEmailAddressToEmailSenderAddressType(emailAddress),
        },
        // @ts-expect-error
        entity: emailAddress.avatarEntity
            ? getRelatedEmailEntity(emailAddress.avatarEntity, emailAddress.display)
            : // This is so annoying, but the related email address type
              // requires an entity value. However, when we actually use this,
              // we don't require an entity. I'd love to change this type, but
              // that invloves refactoring most of our redux form code, and that
              // feels like its beyond what we want to do right now.
              null,
    };
}

function getRelatedEmailEntity(
    email: NutEmailAddressAvatarEntity,
    emailAddress: string
): RelatedEmailAddressesEntity {
    const {__typename, id, name} = email;

    switch (__typename) {
        case 'User':
            return {__typename: 'User', id, name, primaryEmailAddress: emailAddress};
        case 'Contact':
            return {__typename: 'Contact', id, name, primaryEmailAddress: emailAddress};
        case 'Account':
            return {__typename: 'Account', id, name, primaryEmailAddress: emailAddress};
        default:
            return {__typename: 'Contact', id, name, primaryEmailAddress: emailAddress};
    }
}
